import { computed, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';
import { NzMessageService } from 'ng-zorro-antd/message';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, of, tap } from 'rxjs';
import { GetAnswerRequest } from '../../../../../backend/src/core/openai/openai.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { FileUtils } from '../../shared/utils/file-utils';
import { CompanyService } from '../company/company.service';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { OpenaiModel } from '../openai/openai';
import { OpenaiService } from '../openai/openai.service';
import { PersonalityService } from '../personality/personality.service';
import { PostHogEvent } from '../posthog/posthog.event';
import {
  createResumeByProductFunctions,
  createResumeByProductPrompt,
} from '../prompt/resume/create-resume-by-product.prompt';
import { getResumeContentFunctions, getResumeContentPrompt } from '../prompt/resume/get-resume-content.prompt';
import {
  AddResumeByHtmlRequest,
  ResumeGate,
  SaveResumeRequest,
} from './../../../../../backend/src/core/resume/resume.gate';
import { WorkspaceHolderService } from './../workspace/workspace.holder.service';
import { Resume, ResumeSource, ResumeSubData, ResumeType } from './resume';

@Injectable({
  providedIn: 'root',
})
export class ResumeService {
  private _currentChangeCount = 0;

  private _changeSubject = new BehaviorSubject<number>(0);

  constructor(
    private companyService: CompanyService,
    private messageService: NzMessageService,
    private openAiService: OpenaiService,
    private coreItemService: CoreItemService,
    private workspaceHolderService: WorkspaceHolderService,
    private lambdaService: LambdaService,
    private personalityService: PersonalityService
  ) {}

  resumeChanged$ = this._changeSubject.asObservable();

  changeResume() {
    this._changeSubject.next(this._currentChangeCount++);
  }

  personalityList = toSignal(this.personalityService.itemList$(), { initialValue: [] });
  companyList = toSignal(this.companyService.itemList$(), { initialValue: [] });

  getPersonalityList = (resume?: Resume) =>
    computed(() => {
      if (!resume) {
        return [];
      }
      return (
        resume?.subData?.content?.personalityIdList
          ?.map((id) => {
            return this.personalityList().find((personality) => personality.id === id);
          })
          .filter((personality) => !!personality) ?? []
      );
    });

  addResumeByHtml$(html: string, url: string) {
    const request: AddResumeByHtmlRequest = {
      url,
      html,
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
    };

    return this.lambdaService.run$(ResourceName.resume, ResumeGate.actions.addResumeByHtml, request).pipe(
      tap((response) => {
        if (!response?.id) {
          this.messageService.error('Failed to add resume');
          return;
        }
      })
    );
  }

  createResumeByProduct$(productDescription?: string) {
    if (!productDescription) {
      this.messageService.error('Cannot create the AI customer without the product description.');
      return of({ isSuccess: false } as any);
    }

    const customer = {
      firstName: 'Peter',
      lastName: 'Smith',
      gender: 'male',
    };

    const createResumePrompt = createResumeByProductPrompt(productDescription ?? '', customer);
    return this.openAiService
      .getAnswer$({
        messageList: [{ role: 'user', content: createResumePrompt }],
        config: { openaiModel: OpenaiModel.gpt4, functions: createResumeByProductFunctions() },
      })
      .pipe(
        mergeMap((result) => {
          if (!result || !result.mostRecentCompany) {
            result = {
              painPoints: [],
              personalInformation: '',
              workExperience: '',
              mostRecentCompany: 'Peak Mojo',
              mostRecentPosition: 'Software Engineer',
              projectExperience: '',
              education: '',
              skills: [],
              certifications: [],
              awards: [],
              publications: [],
              languages: [],
              interests: [],
              references: [],
              otherDetails: '',
            };
          }

          const companyList = this.companyList();

          const parentId = companyList.find((c) => c.title === result.mostRecentCompany)?.id;

          result.portraitImageUrl = 'https://beta.peakmojo.ai/assets/images/ai-profile/peter_v1.png';
          result.firstName = customer.firstName;
          result.lastName = customer.lastName;
          result.voice = 'aura-zeus-en';

          return this.addResume$({ source: ResumeSource.ai, content: result }, ResumeType.ai, parentId);
        })
      );
  }

  getResumeById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Resume));
  }

  updateResume$(resume: Resume) {
    posthog.capture(PostHogEvent.resumeUpdate);
    return this.coreItemService.updateCoreItem$(resume).pipe(
      tap(() => {
        this.changeResume();
      })
    );
  }

  resumeList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({ coreTypeList: [CoreItemType.resume] }).pipe(
          map((list) => {
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
            return list as Resume[];
          })
        );
      })
    );
  }

  uploadResume$(resumeId: string, fileName: string, fileData: File) {
    return FileUtils.readAsString$(fileData).pipe(
      mergeMap((base64Content) => {
        const parts = base64Content.split(',');
        const noHeadContent = parts[1];
        const contentType = parts[0].split(';')[0].split(':')[1];

        posthog.capture(PostHogEvent.resumeAdd, { fileSize: fileData.size });

        const request: SaveResumeRequest = {
          resumeId,
          workspaceId: this.workspaceHolderService.currentWorkspace().id,
          fileName,
          base64Content: noHeadContent,
          contentType,
        };
        return this.lambdaService.run$(ResourceName.resume, ResumeGate.actions.saveResume, request).pipe(
          tap(() => {
            this.changeResume();
          })
        );
      })
    );
  }

  addResumeFromTextInput$(text: string, type: ResumeType, config: { resumeSource: ResumeSource; url?: string }) {
    const request: GetAnswerRequest = {
      messageList: [
        {
          role: 'user',
          content: getResumeContentPrompt(text),
        },
      ],
      config: { openaiModel: OpenaiModel.gpt4, functions: getResumeContentFunctions() },
    };

    return this.openAiService.getAnswer$(request).pipe(
      mergeMap((result) => {
        const subData: ResumeSubData = {
          source: config.resumeSource,
          url: config.url,
          content: result,
        };

        if (subData.content?.gender === 'male') {
          subData.content!.voice = 'aura-orion-en';

          if (!subData.content!.portraitImageUrl) {
            subData.content!.portraitImageUrl = 'https://beta.peakmojo.ai/assets/images/ai-profile/peter_v1.png';
          }
        } else {
          subData.content!.voice = 'aura-asteria-en';
          if (!subData.content!.portraitImageUrl) {
            subData.content!.portraitImageUrl = 'https://beta.peakmojo.ai/assets/images/ai-profile/sofia_v1.png';
          }
        }

        const personality = this.personalityList().find((c) => c.title === (subData.content as any)?.personality);

        subData.content!.personalityIdList = personality ? [personality.id] : undefined;

        delete (subData.content as any).personality;

        if (!subData.content!.personalityIdList && this.personalityList().find((c) => c.title === 'Brief')) {
          subData.content!.personalityIdList = [this.personalityList().find((c) => c.title === 'Brief')!.id];
        }

        return this.addResume$(subData, type);
      })
    );
  }

  addResume$(subData: ResumeSubData, type: ResumeType, parentId?: string) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            parentId,
            type,
            coreType: CoreItemType.resume,
            subData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(this._currentChangeCount++);
          return arr[0] as Resume;
        })
      );
  }

  deleteResumeList$(resumeList: Resume[]) {
    const deleteMetadataList = resumeList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.resumeDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(this._currentChangeCount++);
        })
      );
  }
}
